import { Divider, Ellipsis, Icon, Input, Modal, Toast } from '@seiue/ui'
import { Form } from 'antd'
import { useContext } from 'react'
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from 'react-beautiful-dnd'
import styled from 'styled-components'

import { pluginApi, termApi } from 'packages/sdks-next/growp'
import { dragWeightReorder, dragReorder } from 'packages/utils'

import { TagContextParam } from '../../utils/type'
import { TagContext, allTagName } from '../../utils/util'

/**
 * 分类管理 - 详情
 *
 * @returns 组件
 */
export const TagDrawerDetail: React.FC = () => {
  const { showTag, setDragSchoolPlugins, dragSchoolPlugins } =
    useContext<TagContextParam>(TagContext)

  const isOtherTag = showTag?.name !== allTagName

  const onDragEnd = async (result: DropResult) => {
    if (!result.destination) {
      return
    }

    const newItems = isOtherTag
      ? dragReorder(
          dragSchoolPlugins,
          result.source.index,
          result.destination.index
        )
      : dragWeightReorder(
          dragSchoolPlugins,
          result.source.index,
          result.destination.index
        )

    setDragSchoolPlugins(newItems)
  }

  const { api: removePluginTag } = pluginApi.useUpdateSchoolPlugin({
    reload: [pluginApi.querySchoolPluginTags, pluginApi.querySchoolPlugins],
  })

  const { api: updatePluginTag } = termApi.useUpdateTerm({
    reload: [pluginApi.querySchoolPluginTags],
  })

  return (
    <Box>
      {/* 分类信息 */}
      <TitleBox>
        <Form layout="vertical">
          <Form.Item
            label="分类名称"
            required={true}
          >
            <Input
              defaultValue={showTag?.name}
              disabled={showTag?.isVirtual}
              onPressEnter={async (e) => {
                if (showTag === undefined) return

                const { value } = e.target as HTMLInputElement

                await updatePluginTag({
                  type: showTag.type || '',
                  id: showTag.id,
                  payload: {
                    name: value,
                  },
                })

                Toast.success('已修改分类名称')
              }}
            />
          </Form.Item>
        </Form>
      </TitleBox>
      <Divider />
      {/* 分类下应用列表 */}
      {dragSchoolPlugins.length === 0 ? (
        <Empty>{'暂无应用'}</Empty>
      ) : (
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="droppable">
            {(provided) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
              >
                {dragSchoolPlugins.map((item, index) => {
                  return (
                    <Draggable
                      key={`${item.id}-${index}-detail`}
                      draggableId={String(item.id)}
                      index={index}
                    >
                      {(provided, snapshot) => (
                        <>
                          <Item
                            key={`${item.id}-${index}-detail`}
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            style={provided.draggableProps.style}
                            $isDragging={snapshot.isDragging}
                          >
                            <Icon
                              icon="Move"
                              size={14}
                              style={{
                                cursor: snapshot.isDragging
                                  ? 'grabbing'
                                  : 'grab',
                                marginRight: '8px',
                              }}
                            />
                            <ItemBox>
                              <ImgBox src={item.icon || ''} />
                              <DescBox>
                                <Title>{item.label}</Title>
                                <Ellipsis
                                  text={item.description}
                                  style={{ fontSize: 12 }}
                                />
                              </DescBox>
                            </ItemBox>
                            {showTag?.isVirtual ? null : (
                              <Icon
                                icon="Close"
                                size={20}
                                onClick={async () => {
                                  Modal.confirm({
                                    title: '移出分类',
                                    content:
                                      '正在把应用移出分类，将被归类到未分类应用。确定移出吗？',
                                    onOk: async () => {
                                      await removePluginTag({
                                        id: item.id,
                                        payload: {
                                          label: item.label || '',
                                          tagId: 0,
                                        },
                                      })
                                      Toast.success('已移出分类')

                                      setDragSchoolPlugins(
                                        dragSchoolPlugins.filter(
                                          (e) => e.id !== item.id
                                        )
                                      )
                                    },
                                  })
                                }}
                              />
                            )}
                          </Item>
                        </>
                      )}
                    </Draggable>
                  )
                })}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}
    </Box>
  )
}

const Box = styled.div`
  padding: 0 16px;
`

const TitleBox = styled.div`
  width: 100%;
  height: 102px;
  display: flex;
  flex-direction: column;
  padding: 20px 0;
`

const Item = styled.div<{
  $isDragging?: boolean
}>`
  &:hover {
    background-color: ${(props) => props.theme.brand._5};
  }
  display: flex;
  align-items: center;
  width: 100%;
  border-radius: 8px;
  background-color: ${(props) =>
    props.$isDragging ? props.theme.brand._5 : '#fff'};

  padding: 20px 4px;
  color: ${(props) => props.theme.text._3};
  justify-content: space-between;
  height: 56px;
`

const ItemBox = styled.div`
  display: flex;
  gap: 8px;
  height: 40px;
  width: 100%;
  align-items: center;
  color: ${(props) => props.theme.text._3};
`

const ImgBox = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  border: 1px solid ${(props) => props.theme.border.greyNormal};
`

const DescBox = styled.div`
  max-width: 258px;
  height: 40px;
  display: flex;
  flex-direction: column;
`

const Title = styled.div`
  font-size: 14px;
  font-weight: 500;
  line-height: 22px;
  height: 22px;
  color: ${(props) => props.theme.text._1};
`

const Empty = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  padding: 200px 122px;
  flex-grow: 1;
  align-self: stretch;
  width: 368px;
  height: 336px;
  color: ${(props) => props.theme.text._3};
`
